import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import interesadosService from "../../../../services/interesadosService";
import bitacoraService from "../../../../services/bitacoraService";
import empresasService from "../../../../services/empresasService";
import fuentesOrigenService from "../../../../services/fuentesOrigenService";
import novedadesService from "../../../../services/novedadesService";
import referentesService from "../../../../services/referentesService";
import { showToast } from "../../../../utils/showToast";
import { extractPermissionCodes } from "../../../../utils/permissions";
import { getSocket, initSocket } from "../../../../utils/socketClient";
import {
  buildBitacoraRows,
  getLocalDateString,
  getSortTimestamp,
  initialRegistro,
  normalizeInteresados,
  obtenerNombreFormulario,
  toDateInputFormat,
  toMysqlDateTime,
  toTimeDisplay,
} from "../adapters/bitacoraAdapter";

// Al entrar a Bitacora se disparan 5 peticiones casi al mismo tiempo (interesados,
// bitacora, empresas, fuentes-origen, novedades). Contra una red compartida/congestionada
// (p.ej. la misma red que usan los estudiantes) una rafaga de conexiones nuevas simultaneas
// puede hacer que alguna se pierda incluso despues de los reintentos de httpClient, dejando
// selects vacios sin aviso. Escalonar el inicio de cada peticion reduce esa rafaga.
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const resolveTipoReferido = (clave) => {
  if (!clave) return "";
  if (clave === "docente") return "docente";
  if (["estudiante", "administrativo", "externo"].includes(clave)) return clave;
  return "";
};

export default function useBitacoraLogic(currentUser = null) {
  const [filtroFecha, setFiltroFecha] = useState(() => getLocalDateString());
  const [alerta, setAlerta] = useState({ message: "", type: "" });
  const [interesados, setInteresados] = useState([]);
  const [bitacora, setBitacora] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [tabActiva, setTabActiva] = useState("interesado");
  const [editando, setEditando] = useState(false);
  const [registroEditado, setRegistroEditado] = useState(null);
  const [empresasApi, setEmpresasApi] = useState([]);
  const [fuentesApi, setFuentesApi] = useState([]);
  const [novedadesApi, setNovedadesApi] = useState({});
  const [selectedCategoria, setSelectedCategoria] = useState("");
  const [selectedSubcategoria, setSelectedSubcategoria] = useState("");
  const [selectedAccion, setSelectedAccion] = useState("");
  const [referidosOpciones, setReferidosOpciones] = useState([]);
  const [referidoSearchTerm, setReferidoSearchTerm] = useState("");
  const [cargandoReferidos, setCargandoReferidos] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const guardandoRef = useRef(false);
  const [recargando, setRecargando] = useState(false);
  const [bloquearInteresado, setBloquearInteresado] = useState(false);
  const [documentoBloqueado, setDocumentoBloqueado] = useState("");

  const filtroFechaRef = useRef(filtroFecha);
  const recepcionistaId = currentUser?.id || null;
  const codigosPermisos = useMemo(
    () => extractPermissionCodes(Array.isArray(currentUser?.permisos) ? currentUser.permisos : []),
    [currentUser]
  );
  const puedeCrearInteresado = codigosPermisos.includes("interesados.crear");
  const rolNombre = (currentUser?.rol?.nombre || "").toLowerCase();
  const ocultarTransferir = rolNombre === "secretaria";
  const ocultarInformacion = rolNombre === "secretaria";

  const [nuevoRegistro, setNuevoRegistro] = useState(() => initialRegistro());

  const resetFormulario = useCallback(() => {
    setNuevoRegistro(initialRegistro());
    setReferidosOpciones([]);
    setReferidoSearchTerm("");
    setSelectedCategoria("");
    setSelectedSubcategoria("");
    setSelectedAccion("");
    setRegistroEditado(null);
    setEditando(false);
    setTabActiva("interesado");
  }, []);

  const construirBitacora = useCallback(
    (visitas) => buildBitacoraRows(visitas, toDateInputFormat),
    []
  );

  const registrarNovedadSeleccionada = useCallback(
    async (bitacoraId) => {
      if (!bitacoraId || !selectedCategoria || !selectedSubcategoria || !selectedAccion) {
        return null;
      }
      try {
        const matches = await novedadesService.listar({
          categoria: selectedCategoria,
          subcategoria: selectedSubcategoria,
          tipo_accion: selectedAccion,
        });
        const matchId = Array.isArray(matches) && matches[0]?.id;
        if (matchId) {
          await bitacoraService.agregarNovedad({ bitacora_id: bitacoraId, novedad_id: matchId }).catch(() => null);
        }
        return selectedAccion;
      } catch (error) {
        console.error("Error vinculando novedad:", error);
        return null;
      }
    },
    [selectedAccion, selectedCategoria, selectedSubcategoria]
  );

  const recargarDatos = useCallback(async () => {
    setRecargando(true);
    const fecha = filtroFechaRef.current;
    try {
      // Offsets desfasados +100ms respecto a los de dashboardLayout.js (que usa
      // 0/200/400/600ms para menu/modulos/notificaciones/branding) para que,
      // al aterrizar directo en /dashboard/bitacora, las dos rafagas escalonadas
      // independientes no disparen en los mismos instantes.
      const [listaInteresados, listaVisitas, listaEmpresas, listaFuentes, selectsNovedades] = await Promise.all([
        wait(100).then(() => interesadosService.listar()).catch(() => []),
        wait(300).then(() => bitacoraService.listar({ fecha })).catch(() => []),
        wait(500).then(() => empresasService.listar()).catch(() => []),
        wait(700).then(() => fuentesOrigenService.listar()).catch(() => []),
        wait(900).then(() => novedadesService.listSelects()).catch(() => ({})),
      ]);

      setInteresados(normalizeInteresados(listaInteresados));
      if (Array.isArray(listaEmpresas)) setEmpresasApi(listaEmpresas);
      if (Array.isArray(listaFuentes)) setFuentesApi(listaFuentes);
      if (selectsNovedades && Object.keys(selectsNovedades).length) setNovedadesApi(selectsNovedades);

      const mapeadas = construirBitacora(listaVisitas);
      setBitacora(mapeadas);
    } catch (e) {
      console.error("Error cargando datos iniciales:", e);
      setAlerta({ message: "No se pudo actualizar la bitacora.", type: "warning" });
    } finally {
      setRecargando(false);
    }
  }, [construirBitacora]);

  // Carga inicial
  useEffect(() => {
    recargarDatos();
  }, [recargarDatos]);

  // Mantener ref sincronizada y recargar al cambiar fecha
  const prevFiltroFechaRef = useRef(null);
  useEffect(() => {
    filtroFechaRef.current = filtroFecha;
    if (prevFiltroFechaRef.current === null) {
      prevFiltroFechaRef.current = filtroFecha;
      return;
    }
    if (prevFiltroFechaRef.current !== filtroFecha) {
      prevFiltroFechaRef.current = filtroFecha;
      recargarDatos();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroFecha]);

  const refreshTimeoutRef = useRef(null);
  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) return;
    refreshTimeoutRef.current = setTimeout(() => {
      refreshTimeoutRef.current = null;
      recargarDatos();
    }, 350);
  }, [recargarDatos]);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    const socket = getSocket() || initSocket(token);
    if (!socket) return undefined;

    const onBitacoraChanged = () => scheduleRefresh();

    socket.on("bitacora:actualizada", onBitacoraChanged);

    return () => {
      socket.off("bitacora:actualizada", onBitacoraChanged);
    };
  }, [scheduleRefresh]);

  const getBadgeClass = (tipo) => {
    const normalized = typeof tipo === "string"
      ? tipo.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
      : "";
    switch (normalized) {
      case "certificado":
        return "bg-success";
      case "credencial":
        return "bg-info text-dark";
      case "abono":
        return "bg-warning text-dark";
      case "inscripcion":
        return "bg-primary";
      default:
        return "bg-secondary";
    }
  };

  const fuentesLista = useMemo(
    () =>
      (fuentesApi || [])
        .map((f) => f.nombre || f.descripcion || f.codigo || "")
        .filter(Boolean),
    [fuentesApi]
  );

  const empresasLista = useMemo(
    () =>
      (empresasApi || [])
        .map((e) => e.nombre || "")
        .filter(Boolean),
    [empresasApi]
  );

  const categoriasLista = useMemo(() => Object.keys(novedadesApi || {}), [novedadesApi]);
  const subcategoriasLista = useMemo(() => {
    if (!selectedCategoria || !novedadesApi[selectedCategoria]) return [];
    return Object.keys(novedadesApi[selectedCategoria] || {});
  }, [novedadesApi, selectedCategoria]);

  const accionesLista = useMemo(() => {
    if (!selectedCategoria || !selectedSubcategoria || !novedadesApi[selectedCategoria]) return [];
    return novedadesApi[selectedCategoria][selectedSubcategoria] || [];
  }, [novedadesApi, selectedCategoria, selectedSubcategoria]);

  const normalizarTexto = useCallback(
    (val = "") =>
      typeof val === "string"
        ? val
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .trim()
        : "",
    []
  );

  const bitacoraOrdenada = useMemo(
    () =>
      [...bitacora].sort((a, b) => {
        const tsB = getSortTimestamp(b.fecha_visita || b.fecha_fin_visita);
        const tsA = getSortTimestamp(a.fecha_visita || a.fecha_fin_visita);
        return tsB - tsA;
      }),
    [bitacora]
  );

  const filas = useMemo(() => {
    const normalizar = (val = "") =>
      typeof val === "string" ? val.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim() : "";
    return bitacoraOrdenada
      .filter((v) => !filtroFecha || v.fecha_visita_iso === filtroFecha)
      .filter((v) => {
        if (!ocultarInformacion) return true;
        return normalizar(v.novedad) !== "informacion";
      });
  }, [bitacoraOrdenada, filtroFecha, ocultarInformacion]);

  const fuenteActual = normalizarTexto(nuevoRegistro.fuente_origen || "");
  const tipoReferido = resolveTipoReferido(fuenteActual);
  const fuenteHabilitaReferido = ["administrativo", "estudiante", "docente"].includes(fuenteActual);
  const fuenteHabilitaEmpresa = fuenteActual === "empresa";
  const mapReferenteOption = useCallback((ref) => {
    const nombres =
      ref.nombre || ref.referido_nombres || ref.nombres || "";
    const apellidos = ref.apellidos || ref.referido_apellidos || "";
    const documento =
      ref.identificacion ||
      ref.referido_documento ||
      ref.documento_identidad ||
      "";
    const estado =
      ref.tipo || ref.referido_estado || ref.estado_academico || "";
    return {
      id: ref.id,
      nombres,
      apellidos,
      documento_identidad: documento,
      estado_academico: estado,
    };
  }, []);

  const requiereReferido = useMemo(() => {
    const cat = (selectedCategoria || "").toLowerCase();
    const sub = (selectedSubcategoria || "").toLowerCase();
    const acc = (selectedAccion || "").toLowerCase();
    return cat === "otro" && sub === "comision" && acc === "comision";
  }, [selectedAccion, selectedCategoria, selectedSubcategoria]);

  useEffect(() => {
    if (!fuenteHabilitaReferido && !requiereReferido) {
      setReferidoSearchTerm("");
      setReferidosOpciones([]);
      setCargandoReferidos(false);
      setNuevoRegistro((prev) => (prev.referido_por_id ? { ...prev, referido_por_id: null } : prev));
    }
  }, [fuenteHabilitaReferido, requiereReferido]);

  useEffect(() => {
    if (!fuenteHabilitaEmpresa) {
      setNuevoRegistro((prev) => ({
        ...prev,
        empresa: "",
        nuevaEmpresa: { nombre: "" },
      }));
    }
  }, [fuenteHabilitaEmpresa]);

  useEffect(() => {
    let activo = true;
    const termino = (referidoSearchTerm || "").trim();
    const minChars = 2;
    const shouldSearch = (fuenteHabilitaReferido || requiereReferido) && termino.length >= minChars;

    if (!shouldSearch) {
      setCargandoReferidos(false);
      return () => {
        activo = false;
      };
    }

    setCargandoReferidos(true);
    const handler = setTimeout(async () => {
      try {
        const tipoFiltro = tipoReferido || undefined;
        const lista = await referentesService
          .buscar(termino, { tipo: tipoFiltro })
          .catch(() => []);
        if (!activo) return;
        const opciones = (Array.isArray(lista) ? lista : []).map(mapReferenteOption);
        setReferidosOpciones(opciones);
      } catch (error) {
        console.error("No se pudo recuperar la lista de referidos:", error);
        if (activo) setReferidosOpciones([]);
      } finally {
        if (activo) setCargandoReferidos(false);
      }
    }, 350);

    return () => {
      activo = false;
      clearTimeout(handler);
    };
  }, [fuenteHabilitaReferido, mapReferenteOption, referidoSearchTerm, requiereReferido, tipoReferido]);

  const buscarInteresado = async (cedulaInput) => {
    const termino = (cedulaInput || "").trim();
    setNuevoRegistro((prev) => ({ ...prev, documento: termino }));

    const esNumerico = /^\d+$/.test(termino);
    const esBusquedaNombre = !esNumerico && termino.length >= 3;

    if (!termino || (esNumerico && termino.length < 5)) return;

    const cargarEncontrado = (encontrado, documentoBase = termino) => {
      const { nombres, apellidos } = obtenerNombreFormulario(encontrado);
      const docFinal = encontrado.documento || documentoBase || "";
      setNuevoRegistro((prev) => ({
        ...prev,
        nombre: nombres,
        apellidos,
        telefono: encontrado.telefono || "",
        email: encontrado.email || "",
        documento: docFinal,
        fuente_origen: encontrado.fuente_origen || "",
        empresa:
          encontrado.empresa ||
          encontrado.empresa_referencia ||
          encontrado.empresa_origen ||
          encontrado.centro ||
          encontrado.centro_referencia ||
          "",
        referido_por_id: encontrado.referido_por_id || null,
        referido_nombres: encontrado.referido_nombres || "",
        referido_apellidos: encontrado.referido_apellidos || "",
        referido_documento: encontrado.referido_documento || "",
        referido_estado: encontrado.referido_estado || "",
      }));
      setBloquearInteresado(true);
      setDocumentoBloqueado(docFinal);

      if (encontrado.referido_por_id) {
        const agregarOpcion = (ref) => {
          if (!ref) return;
          const option = mapReferenteOption(ref);
          setReferidosOpciones((prev) => {
            const existe = prev.some((r) => String(r.id) === String(option.id));
            return existe ? prev.map((r) => (String(r.id) === String(option.id) ? option : r)) : [...prev, option];
          });
          setNuevoRegistro((prev) => ({
            ...prev,
            referido_nombres: option.nombres || prev.referido_nombres,
            referido_apellidos: option.apellidos || prev.referido_apellidos,
            referido_documento: option.documento_identidad || prev.referido_documento,
            referido_estado: option.estado_academico || prev.referido_estado,
          }));
        };

        const nombreRef = `${encontrado.referido_nombres || ""} ${encontrado.referido_apellidos || ""}`.trim();
        if (nombreRef || encontrado.referido_documento) {
          agregarOpcion({
            id: encontrado.referido_por_id,
            nombre: encontrado.referido_nombres,
            identificacion: encontrado.referido_documento,
            tipo: encontrado.referido_estado,
          });
        } else {
          referentesService.obtener(encontrado.referido_por_id).then(agregarOpcion).catch(() => null);
        }
      }
    };

    let encontrado = null;

    if (esNumerico) {
      try {
        const respuesta = await interesadosService.buscarPorDocumento(termino);
        if (respuesta) {
          const normalizados = normalizeInteresados([respuesta]);
          encontrado = normalizados[0] || respuesta;
          setInteresados((prev) => {
            const existe = prev.some((i) => String(i.id) === String(encontrado.id));
            if (existe) {
              return prev.map((i) => (String(i.id) === String(encontrado.id) ? { ...i, ...encontrado } : i));
            }
            return [...prev, encontrado];
          });
        }
      } catch (error) {
        if (error?.status !== 404) {
          console.error("No se pudo recuperar al interesado por documento:", error);
        }
      }

      if (!encontrado) {
        encontrado = interesados.find((i) => (i.documento || "").trim() === termino);
      }
    } else if (esBusquedaNombre) {
      const terminoNormalizado = normalizarTexto(termino);
      const coincidencias = interesados.filter((i) => {
        const nombreCompleto = normalizarTexto(`${i.nombres || ""} ${i.apellidos || ""}`);
        const docNormalizado = normalizarTexto(i.documento || "");
        return nombreCompleto.includes(terminoNormalizado) || docNormalizado.includes(terminoNormalizado);
      });

      if (coincidencias.length === 1) {
        encontrado = coincidencias[0];
      } else if (coincidencias.length > 1) {
        showToast({ type: "warning", message: "Se encontraron varias coincidencias, especifica mejor la busqueda." });
        return;
      } else {
        showToast({ type: "warning", message: "No se encontro un interesado con ese nombre o cedula." });
        return;
      }
    }

    if (encontrado) {
      cargarEncontrado(encontrado, termino);
      showToast({ type: "success", message: "Interesado encontrado y cargado." });
    } else {
      showToast({ type: "warning", message: "No se encontro un interesado con esa cedula." });
    }
  };

  const openNuevo = () => {
    resetFormulario();
    setBloquearInteresado(false);
    setDocumentoBloqueado("");
    setShowModal(true);
  };

  const handleSalida = async (id) => {
    try {
      await bitacoraService.salida(id);
      const ahoraDate = new Date();
      const ahoraHora = ahoraDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      const ahoraMysql = toMysqlDateTime(ahoraDate);
      setBitacora((prev) =>
        prev.map((v) =>
          v.id === id
            ? {
                ...v,
                salida: ahoraHora,
                fecha_fin_visita: ahoraMysql,
              }
            : v
        )
      );
      showToast({ type: "success", message: "Salida registrada correctamente." });
    } catch (e) {
      console.error("Error registrando salida:", e);
      showToast({ type: "danger", message: "No se pudo registrar la salida." });
    }
  };

  const handleEditar = async (registro) => {
    const interesado = interesados.find((i) => i.id === registro.interesado_id);
    const { nombres: nombreBase, apellidos: apellidoBase } = obtenerNombreFormulario(interesado);
    const baseDatos = {
      nombre: nombreBase,
      apellidos: apellidoBase,
      documento: interesado?.documento || "",
      telefono: interesado?.telefono || "",
      email: interesado?.email || "",
      fuente_origen: interesado?.fuente_origen || "",
      empresa:
        interesado?.empresa ||
        interesado?.empresa_referencia ||
        interesado?.empresa_origen ||
        interesado?.centro ||
        interesado?.centro_referencia ||
        "",
      observacion: registro.observacion || "",
      convertido_a_prematricula: !!registro.convertido_a_prematricula,
      transferido: !!registro.transferido,
      referido_por_id: registro?.referido_por_id || interesado?.referido_por_id || null,
      referido_nombres: registro?.referido_nombres || interesado?.referido_nombres || "",
      referido_apellidos: registro?.referido_apellidos || interesado?.referido_apellidos || "",
      referido_documento: registro?.referido_documento || interesado?.referido_documento || "",
      referido_estado: registro?.referido_estado || interesado?.referido_estado || "",
    };
    setNuevoRegistro((prev) => ({ ...prev, ...baseDatos }));
    setBloquearInteresado(true);
    setDocumentoBloqueado(baseDatos.documento || "");
    if (baseDatos.referido_por_id) {
      setReferidosOpciones((prev) => {
        const existe = prev.some((r) => String(r.id) === String(baseDatos.referido_por_id));
        if (existe) return prev;
        const option = mapReferenteOption({
          id: baseDatos.referido_por_id,
          nombre: baseDatos.referido_nombres || "",
          identificacion: baseDatos.referido_documento || "",
          tipo: baseDatos.referido_estado || "",
        });
        return [...prev, option];
      });
    }
    try {
      const [detalle, listadoNovedades] = await Promise.all([
        bitacoraService.detalle(registro.id).catch(() => null),
        bitacoraService.listarNovedades(registro.id).catch(() => []),
      ]);

      if (detalle) {
        setNuevoRegistro((prev) => ({
          ...prev,
          observacion: detalle.observacion || baseDatos.observacion,
          convertido_a_prematricula: !!detalle.convertido_a_prematricula,
          transferido: !!detalle.transferido,
          referido_por_id: detalle.referido_por_id || null,
        }));

        if (detalle.referido_por_id && (detalle.referido_nombres || detalle.referido_apellidos)) {
          setReferidosOpciones((prev) => {
            const existe = prev.some((r) => r.id === detalle.referido_por_id);
            if (existe) return prev;
            return [
              ...prev,
              {
                id: detalle.referido_por_id,
                nombres: detalle.referido_nombres || "",
                apellidos: detalle.referido_apellidos || "",
                documento_identidad: detalle.referido_documento || "",
              },
            ];
          });
        }
      }

      if (Array.isArray(listadoNovedades) && listadoNovedades.length > 0) {
        const n = listadoNovedades[listadoNovedades.length - 1];
        setSelectedCategoria(n.categoria || "");
        setSelectedSubcategoria(n.subcategoria || "");
        setSelectedAccion(n.tipo_accion || "");
      } else {
        setSelectedCategoria("");
        setSelectedSubcategoria("");
        setSelectedAccion("");
      }
    } catch (err) {
      console.error(err);
    }
    setEditando(true);
    setRegistroEditado(registro);
    setTabActiva("bitacora");
    setShowModal(true);
  };

  const handleGuardar = async () => {
    if (guardandoRef.current) return;
    guardandoRef.current = true;
    setGuardando(true);
    try {
      const documentoLimpio = (nuevoRegistro.documento || "").trim();
      const telefonoLimpio = (nuevoRegistro.telefono || "").trim();
      const nombreLimpio = (nuevoRegistro.nombre || "").trim();
      const apellidosLimpios = (nuevoRegistro.apellidos || "").trim();
      const nombreUpper = nombreLimpio.toUpperCase();
      const apellidosUpper = apellidosLimpios.toUpperCase();
      const emailLimpio = (nuevoRegistro.email || "").trim();
      const observacionLimpia = (nuevoRegistro.observacion || "").trim();

      const cedulaValida = (() => {
        if (!/^\d{10}$/.test(documentoLimpio)) return false;
        const prefijo = Number(documentoLimpio.slice(0, 2));
        return (prefijo >= 1 && prefijo <= 24) || prefijo === 30;
      })();
      const emailValido = !emailLimpio || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailLimpio);

      if (!cedulaValida) {
        setAlerta({
          message: "La cedula debe tener 10 digitos y comenzar con un codigo valido (01-24 o 30).",
          type: "warning",
        });
        return;
      }
      if (!nombreLimpio || !apellidosLimpios) {
        setAlerta({ message: "Complete nombres y apellidos del interesado.", type: "warning" });
        return;
      }
      if (emailLimpio && !emailValido) {
        setAlerta({ message: "Ingrese un correo valido.", type: "warning" });
        return;
      }
      if (!recepcionistaId) {
        setAlerta({ message: "No se pudo identificar al usuario en sesion.", type: "danger" });
        return;
      }

      const interesadoExistente =
        interesados.find((i) => (i.documento || "").trim() === documentoLimpio) ||
        interesados.find((i) => i.id === registroEditado?.interesado_id);

      if (!interesadoExistente && !puedeCrearInteresado) {
        setTabActiva("interesado");
        setAlerta({
          message: "No tienes permiso para crear interesados. Busca y selecciona uno existente.",
          type: "warning",
        });
        return;
      }
      const accionLimpia = (selectedAccion || "").trim();
      if (!accionLimpia) {
        setTabActiva("bitacora");
        setAlerta({ message: "Selecciona la accion de la bitacora antes de guardar.", type: "warning" });
        return;
      }

      const fuenteSeleccionadaId =
        (fuentesApi.find((f) => (f.nombre || f.descripcion || f.codigo) === nuevoRegistro.fuente_origen)?.id) || null;

      let empresaNombre = "";
      let empresaSeleccionadaId = null;
      empresaNombre = (nuevoRegistro.empresa || "").trim();
      if (empresaNombre) {
        const normalizada = normalizarTexto(empresaNombre);
        const empresaEncontrada = empresasApi.find(
          (e) => normalizarTexto(e.nombre || "") === normalizada
        );
        if (empresaEncontrada?.id) {
          empresaNombre = empresaEncontrada.nombre || empresaNombre;
          empresaSeleccionadaId = empresaEncontrada.id;
        } else {
          const empresaCreada = await empresasService
            .crear({
              nombre: empresaNombre,
              ruc: null,
              telefono: null,
              email: null,
              tipo: "externa",
              activo: 1,
            })
            .catch((error) => {
              console.error("Error creando empresa:", error);
              return null;
            });

          if (!empresaCreada?.id) {
            setAlerta({
              message: "No se pudo registrar la nueva empresa. Intenta nuevamente.",
              type: "danger",
            });
            return;
          }

          empresaNombre = empresaCreada.nombre || empresaNombre;
          empresaSeleccionadaId = empresaCreada.id;
          setEmpresasApi((prev) => {
            const existe = prev.some((e) => String(e.id) === String(empresaCreada.id));
            if (existe) return prev;
            return [...prev, empresaCreada];
          });
        }
      }
      const referidoSeleccionadoId = nuevoRegistro.referido_por_id ? Number(nuevoRegistro.referido_por_id) : null;

      let interesado = interesadoExistente;
      if (!interesado) {
        if (!puedeCrearInteresado) {
          setAlerta({
            message: "No tienes permiso para crear interesados. Busca y selecciona uno existente.",
            type: "warning",
          });
          return;
        }

        const creado = await interesadosService
          .crear({
            nombres: nombreUpper,
            apellidos: apellidosUpper,
            documento: documentoLimpio || null,
            telefono: telefonoLimpio,
            email: emailLimpio || null,
            fuente_origen_id: fuenteSeleccionadaId,
            empresa_origen_id: empresaSeleccionadaId,
            referido_por_id: referidoSeleccionadoId,
          })
          .catch(() => null);

        if (!creado?.id) {
          setAlerta({
            message: "No se pudo registrar el interesado. Verifica permisos o intenta nuevamente.",
            type: "danger",
          });
          return;
        }

        const nuevoId = creado.id;
        const baseInteresado = {
          id: nuevoId,
          nombres: nombreUpper,
          apellidos: apellidosUpper,
          documento: documentoLimpio,
          telefono: telefonoLimpio,
          email: emailLimpio,
          fuente_origen: nuevoRegistro.fuente_origen || "N/A",
          empresa: empresaNombre,
          empresa_origen: empresaNombre,
          estado_interes: "interesado",
          referido_por_id: referidoSeleccionadoId,
        };
        const normalizados = normalizeInteresados([baseInteresado]);
        interesado = normalizados[0] || baseInteresado;
        setInteresados((prev) => [...prev, interesado]);
      } else if (referidoSeleccionadoId && interesado.referido_por_id !== referidoSeleccionadoId) {
        await interesadosService
          .actualizar(interesado.id, {
            nombres: interesado.nombres,
            apellidos: interesado.apellidos,
            documento: interesado.documento,
            telefono: interesado.telefono,
            email: interesado.email,
            fuente_origen_id: fuenteSeleccionadaId,
            centro_referencia_id: null,
            empresa_origen_id: empresaSeleccionadaId,
            referido_por_id: referidoSeleccionadoId,
          })
          .catch(() => null);
        interesado = { ...interesado, referido_por_id: referidoSeleccionadoId };
        setInteresados((prev) =>
          prev.map((i) => (i.id === interesado.id ? { ...i, referido_por_id: referidoSeleccionadoId } : i))
        );
      }

      const estadoInteresActual = (interesado?.estado_interes || "").toLowerCase();
      const yaConvertido =
        estadoInteresActual === "prematriculado" ||
        estadoInteresActual === "matriculado" ||
        (!!registroEditado && registroEditado.convertido_a_prematricula);
      const debeConvertirAhora = nuevoRegistro.convertido_a_prematricula && !yaConvertido;
      if (debeConvertirAhora) {
        try {
          await interesadosService.convertir(interesado.id, {
            transferToSecretaria: !!nuevoRegistro.transferido,
            prematriculado: true,
          });
        } catch (err) {
          console.error("No se pudo convertir el interesado:", err);
        }
      }

      const transferFlag = !!nuevoRegistro.transferido;

      if (editando && registroEditado) {
        const fuenteVisita = registroEditado.fecha_visita || new Date();
        const fechaVisita = toMysqlDateTime(fuenteVisita) || toMysqlDateTime(new Date());
        const fechaVisitaIso = toDateInputFormat(fuenteVisita) || registroEditado.fecha_visita_iso || null;
        const entradaHora = toTimeDisplay(fuenteVisita) || registroEditado.entrada || "";

        const fechaSalida = toMysqlDateTime(registroEditado.fecha_fin_visita);
        const salidaHora = toTimeDisplay(registroEditado.fecha_fin_visita) || registroEditado.salida || "";

        await bitacoraService.actualizar(registroEditado.id, {
          interesado_id: interesado.id,
          recepcionista_id: recepcionistaId,
          fecha_visita: fechaVisita,
          fecha_fin_visita: fechaSalida,
          observacion: observacionLimpia,
          fuente_origen_id: fuenteSeleccionadaId,
          referido_por_id: referidoSeleccionadoId,
          convertido_a_prematricula: nuevoRegistro.convertido_a_prematricula ? 1 : 0,
          transferido: transferFlag ? 1 : 0,
          accion_final: selectedAccion || null,
        });

        if (registroEditado.novedad !== selectedAccion && selectedAccion && registroEditado.id) {
          await registrarNovedadSeleccionada(registroEditado.id);
        }

        setBitacora((prev) =>
          prev.map((b) =>
            b.id === registroEditado.id
              ? {
                  ...b,
                  fecha_visita: fechaVisita || b.fecha_visita,
                  fecha_fin_visita: fechaSalida ?? b.fecha_fin_visita,
                  fecha_visita_iso: fechaVisitaIso || b.fecha_visita_iso,
                  entrada: entradaHora || b.entrada,
                  salida: fechaSalida ? salidaHora || b.salida : b.salida,
                  observacion: observacionLimpia,
                  novedad: selectedAccion || b.novedad,
                  transferido: transferFlag,
                  convertido_a_prematricula: nuevoRegistro.convertido_a_prematricula,
                  referido_por_id: referidoSeleccionadoId,
                }
              : b
          )
        );
      } else {
        const creada = await bitacoraService.crear({
          interesado_id: interesado.id,
          recepcionista_id: recepcionistaId,
          observacion: observacionLimpia,
          fuente_origen_id: fuenteSeleccionadaId,
          referido_por_id: referidoSeleccionadoId,
          convertido_a_prematricula: nuevoRegistro.convertido_a_prematricula ? 1 : 0,
          transferido: transferFlag ? 1 : 0,
          accion_final: selectedAccion || null,
          ...(nuevoRegistro.fecha_visita_override ? { fecha_visita: nuevoRegistro.fecha_visita_override } : {}),
        });

        if (!creada?.id) {
          setAlerta({
            message: "No se pudo registrar la bitacora. Verifica la conexion e intenta nuevamente.",
            type: "danger",
          });
          return;
        }

        const visitaId = creada.id;
        let textoNovedad = selectedAccion || "N/A";
        if (creada?.id) {
          const registrado = await registrarNovedadSeleccionada(creada.id);
          if (registrado) textoNovedad = registrado;
        }

        const fechaVisitaDate = nuevoRegistro.fecha_visita_override ? new Date(nuevoRegistro.fecha_visita_override) : new Date();
        const fechaVisita = toMysqlDateTime(fechaVisitaDate);
        const fechaVisitaIso = toDateInputFormat(fechaVisitaDate);
        const entradaHora = toTimeDisplay(fechaVisitaDate);
        const nuevaVisita = {
          id: visitaId,
          interesado_id: interesado.id,
          estado_interes: interesado.estado_interes || "",
          referido_por_id: referidoSeleccionadoId,
          fecha_visita: fechaVisita,
          fecha_fin_visita: null,
          fecha_visita_iso: fechaVisitaIso,
          entrada: entradaHora,
          salida: "",
          observacion: observacionLimpia,
          novedad: textoNovedad,
          transferido: transferFlag,
          convertido_a_prematricula: nuevoRegistro.convertido_a_prematricula,
        };
        setBitacora((prev) => {
          const existe = prev.some((b) => b.id === visitaId);
          if (existe) return prev;
          return [...prev, nuevaVisita];
        });
      }

      setAlerta({
        message: editando ? "Registro actualizado correctamente." : "Registro guardado correctamente.",
        type: "success",
      });

      setShowModal(false);
      resetFormulario();
    } catch (e) {
      console.error("Error guardando registro:", e);
      setAlerta({
        message: "No se pudo guardar la bitacora. Intenta nuevamente.",
        type: "danger",
      });
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  return {
    filtroFecha,
    setFiltroFecha,
    alerta,
    setAlerta,
    interesados,
    bitacora,
    filas,
    showModal,
    setShowModal,
    tabActiva,
    setTabActiva,
    editando,
    setEditando,
    registroEditado,
    setRegistroEditado,
    nuevoRegistro,
    setNuevoRegistro,
    empresasApi,
    fuentesApi,
    novedadesApi,
    selectedCategoria,
    setSelectedCategoria,
    selectedSubcategoria,
    setSelectedSubcategoria,
    selectedAccion,
    setSelectedAccion,
    requiereReferido,
    fuenteHabilitaReferido,
    fuenteHabilitaEmpresa,
    referidosOpciones,
    referidoSearchTerm,
    setReferidoSearchTerm,
    cargandoReferidos,
    recargando,
    guardando,
    ocultarTransferir,
    categoriasLista,
    subcategoriasLista,
    accionesLista,
    getBadgeClass,
    buscarInteresado,
    fuentesLista,
    empresasLista,
    openNuevo,
    handleEditar,
    handleGuardar,
    handleSalida,
    recargarDatos,
    bloquearInteresado,
    documentoBloqueado,
    setBloquearInteresado,
  };
}
